import { HttpClient } from "@angular/common/http";
import { Component, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Endpoints } from "src/app/shared/config/endpoints";

declare const require: any;
const COUNTRIES_WITH_STATES = require("../../assets/countries.json") as any[];

@Component({
  selector: "app-public-lead-form",
  templateUrl: "./public-lead-form.component.html",
  styleUrls: ["./public-lead-form.component.css"],
})
export class PublicLeadFormComponent implements OnInit {
  slug = "";
  form: any;
  values = {};
  loading = true;
  submitting = false;
  submitted = false;
  errorMessage = "";
  successMessage = "";
  countryOptions: string[] = [];
  stateOptions: string[] = [];
  searchValues = {};
  searchOpen = {};
  fileUploadErrors = {};
  fileUploadNames = {};
  fileUploading = {};

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private endpoints: Endpoints,
  ) {}

  ngOnInit() {
    this.loadCountryStateOptions();
    this.route.params.subscribe((params) => {
      this.slug = params.slug;
      this.fetchForm();
    });
  }

  loadCountryStateOptions() {
    this.countryOptions = COUNTRIES_WITH_STATES
      .map((country) => country.name)
      .filter((name) => name);
    this.stateOptions = COUNTRIES_WITH_STATES
      .reduce((states, country) => states.concat(country.states || []), [])
      .map((state) => state.name)
      .filter((name, index, states) => name && states.indexOf(name) === index);
  }

  fetchForm() {
    this.loading = true;
    this.errorMessage = "";

    this.http.get(`${this.endpoints.fourFourSixEndpoint}/public/forms/${this.slug}`).subscribe(
      (res: any) => {
        if (res && res.success) {
          this.form = res.payload;
        } else {
          this.errorMessage = res && res.payload ? res.payload : "Form not found.";
        }
      },
      (err) => {
        this.errorMessage = this.getErrorMessage(err, "Form not found.");
      }
    ).add(() => {
      this.loading = false;
    });
  }

  getValue(field) {
    return this.values[field.key] || "";
  }

  setValue(field, value) {
    this.values[field.key] = value;
  }

  hasTextLengthValue(field, key) {
    const value = field && field.validation ? field.validation[key] : null;
    return value !== undefined && value !== null && value !== "";
  }
  isCheckboxChecked(field, option): boolean {
    const val = this.values[field.key];
    if (field.key === "industry") return val === option;
    return Array.isArray(val) ? val.indexOf(option) >= 0 : val === option;
  }

  setCheckboxValue(field, option, checked) {
    if (field.key === "industry") {
      this.values[field.key] = checked ? option : "";
      return;
    }
    const values = Array.isArray(this.values[field.key]) ? this.values[field.key] : [];
    this.values[field.key] = checked
      ? values.concat(option)
      : values.filter((item) => item !== option);
  }

  isProfileImageField(field) {
    return field && field.key === "profileImage";
  }

  isAllowedProfileImage(file) {
    const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
    const allowedExtensions = /\.(png|jpe?g)$/i;
    return file && allowedTypes.indexOf(file.type) >= 0 && allowedExtensions.test(file.name || "");
  }

  handleFileChange(field, event) {
    const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
    this.fileUploadErrors[field.key] = "";
    this.fileUploadNames[field.key] = file ? file.name : "";

    if (!file) {
      this.setValue(field, "");
      return;
    }

    if (this.isProfileImageField(field)) {
      this.uploadProfileImage(field, file, event.target);
      return;
    }

    this.setValue(field, file.name);
  }

  uploadProfileImage(field, file, input) {
    if (!this.isAllowedProfileImage(file)) {
      this.fileUploadErrors[field.key] = "Please upload a PNG or JPG image.";
      this.fileUploadNames[field.key] = "";
      this.setValue(field, "");
      if (input) input.value = "";
      return;
    }

    const formData = new FormData();
    formData.append("image", file);
    this.fileUploading[field.key] = true;

    this.http.post(`${this.endpoints.profileUpload}${this.endpoints.imageUpload.getPostImage}`, formData).subscribe(
      (res: any) => {
        const payload = res && res.payload ? res.payload : res;
        const imageValue = payload && (payload.request && payload.request.uri
          ? payload.request.uri
          : payload.url || payload.uri || payload.path || payload.imageName);

        if (imageValue) {
          this.setValue(field, imageValue);
        } else {
          this.fileUploadErrors[field.key] = "Image uploaded, but no image URL was returned.";
          this.setValue(field, "");
        }
      },
      (err) => {
        this.fileUploadErrors[field.key] = this.getErrorMessage(err, "Could not upload image.");
        this.fileUploadNames[field.key] = "";
        this.setValue(field, "");
        if (input) input.value = "";
      }
    ).add(() => {
      this.fileUploading[field.key] = false;
    });
  }

  getFieldName(field, index) {
    return `${field.key}-${index}`;
  }

  getFieldOptions(field) {
    if (this.isCountryField(field)) {
      return this.countryOptions;
    }

    if (this.isStateField(field)) {
      return this.stateOptions;
    }

    if (this.isGenderField(field) && (!field.options || !field.options.length)) {
      return ["Male", "Female", "Other"];
    }

    if (field && (field.type === "radio" || field.type === "checkbox") && (!field.options || !field.options.length)) {
      return ["Option 1", "Option 2"];
    }

    return field && Array.isArray(field.options) ? field.options : [];
  }

  isCountryField(field) {
    return field && (field.dynamicOptions === "countries" || field.key === "country");
  }

  isStateField(field) {
    return field && (
      field.dynamicOptions === "states"
      || field.key === "state"
      || field.key === "stateOfOrigin"
    );
  }

  isGenderField(field) {
    return field && field.key === "gender";
  }

  isSearchableLocationField(field) {
    return this.isCountryField(field) || this.isStateField(field);
  }

  getSearchValue(field) {
    return this.searchValues[field.key] || "";
  }

  setSearchValue(field, value) {
    this.searchValues[field.key] = value || "";
    this.searchOpen[field.key] = !!value;
    this.setValue(field, value || "");
  }

  openSearch(field) {
    if (this.getSearchValue(field)) {
      this.searchOpen[field.key] = true;
    }
  }

  getFilteredOptions(field) {
    if (this.searchOpen[field.key] === false) return [];

    const search = this.getSearchValue(field).toLowerCase().trim();
    if (!search) return [];

    return this.getFieldOptions(field)
      .filter((option) => String(option).toLowerCase().indexOf(search) >= 0)
      .slice(0, 12);
  }

  selectSearchOption(field, option) {
    this.searchValues[field.key] = option;
    this.searchOpen[field.key] = false;
    this.setValue(field, option);
  }
  normalizeWebsiteUrl(url: string): string {
    if (!url || typeof url !== "string") return "";
    let trimmed = url.trim();
    if (!trimmed) return "";
    if (/^http:\/\//i.test(trimmed)) {
      return trimmed.replace(/^http:\/\//i, "https://");
    }
    if (!/^https:\/\//i.test(trimmed)) {
      return `https://${trimmed}`;
    }
    return trimmed;
  }

  isCheckboxRequired(field): boolean {
    if (!field || !field.required) return false;
    const val = this.values[field.key];
    if (field.key === "industry") {
      return !val;
    }
    return !Array.isArray(val) || val.length === 0;
  }

  submitForm() {
    if (!this.form || this.form.status !== "active" || this.hasPendingUploads()) return;

    for (const field of this.form.fields || []) {
      if (field.required && field.type === "checkbox" && this.isCheckboxRequired(field)) {
        this.errorMessage = `${field.label} is required.`;
        return;
      }
      if ((field.type === "website" || field.key === "website") && this.values[field.key]) {
        this.values[field.key] = this.normalizeWebsiteUrl(this.values[field.key]);
      }
    }

    this.submitting = true;
    this.errorMessage = "";

    this.http.post(`${this.endpoints.fourFourSixEndpoint}/public/forms/${this.slug}/submissions`, {
      values: this.values,
    }).subscribe(
      (res: any) => {
        if (res && res.success) {
          this.submitted = true;
          this.successMessage = res.payload && res.payload.message
            ? res.payload.message
            : this.form.successMessage || "Submission received.";
        } else {
          this.errorMessage = res && res.payload ? res.payload : "Could not submit form.";
        }
      },
      (err) => {
        this.errorMessage = this.getErrorMessage(err, "Could not submit form.");
      }
    ).add(() => {
      this.submitting = false;
    });
  }

  hasPendingUploads() {
    return Object.keys(this.fileUploading).some((key) => this.fileUploading[key]);
  }

  getErrorMessage(error, fallback) {
    if (error && error.error) {
      return error.error.payload || error.error.message || error.error.error || fallback;
    }

    return fallback;
  }
}
